import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import Lightbox from "../components/Lightbox";
import { MONDE_BY_SLUG, MONDES } from "../lib/pulaar";

export default function MondeDetail() {
  const { slug } = useParams();
  const monde = MONDE_BY_SLUG[slug];
  const { t } = useTranslation();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!monde) return <Navigate to="/archives" replace />;

  const otherMondes = MONDES.filter((m) => m.slug !== slug);
  const ns = `mondes.${slug}`;

  // Translated content (fallbacks to FR fields if a key is missing)
  const subtitle = t(`${ns}.subtitle`, { defaultValue: monde.subtitle });
  const quote = t(`${ns}.quote`, { defaultValue: monde.quote });
  const storyTitle = t(`${ns}.storyTitle`, { defaultValue: monde.storyTitle });
  const story = t(`${ns}.story`, { returnObjects: true, defaultValue: monde.story });
  const storyArr = Array.isArray(story) ? story : monde.story;

  // OG description : 2 premières phrases du teaser pour respecter ≤200 caractères
  const ogDescription = t(`${ns}.teaser`, { defaultValue: monde.teaser }).slice(0, 200);

  return (
    <main
      data-testid={`monde-detail-${monde.slug}`}
      className="relative bg-[#FBF8EF]"
    >
      <SEO
        title={`${monde.name} — ${subtitle}`}
        description={`Pulaar · Archives — ${monde.name}. ${ogDescription}`}
        path={`/archives/${monde.slug}`}
        image={monde.heroImage}
        imageAlt={`Pulaar — Chapitre ${monde.name} : ${subtitle}`}
        type="article"
      />
      {/* Hero cover */}
      <section className="relative h-[80vh] md:h-[92vh] overflow-hidden">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
          src={monde.heroImage}
          alt={monde.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
        <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-14 text-[#FBF8EF]">
          <div className="flex items-start justify-between text-[11px] tracking-[0.32em] uppercase">
            <span>— {monde.symbol} · {t("nav.archives")}</span>
            <span className="hidden md:block">
              {t("mondeDetail.photoCredit")} ·{" "}
              {monde.credit.photoUrl ? (
                <a
                  href={monde.credit.photoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-credit-photo-link"
                  className="link-underline"
                >
                  {monde.credit.photo}
                </a>
              ) : (
                monde.credit.photo
              )}
            </span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="font-serif text-[72px] md:text-[140px] lg:text-[180px] leading-[0.9] tracking-[-0.02em]"
          >
            {monde.name}
          </motion.h1>
        </div>
      </section>

      {/* Quote */}
      <section className="mx-auto max-w-[1100px] px-6 md:px-12 py-28 md:py-40 text-center">
        <Reveal>
          <p className="font-serif italic text-2xl md:text-4xl leading-[1.3] text-[#121212] text-balance">
            « {quote} »
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-[11px] tracking-[0.32em] uppercase text-[#5C4033]">
            {monde.quotePulaar}
          </p>
        </Reveal>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 pb-28 md:pb-40">
        <Reveal className="md:col-span-5">
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-6">
            {t("mondeDetail.storyEyebrow")}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-[#121212]">
            {storyTitle}
          </h2>
        </Reveal>
        <div className="md:col-span-7 space-y-6 text-base md:text-lg leading-[1.85] text-[#4A4641]">
          {storyArr.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p>{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <p className="pt-6 text-[11px] tracking-[0.32em] uppercase text-[#5C4033]">
              {t("mondeDetail.photoCredit")} ·{" "}
              {monde.credit.photoUrl ? (
                <a
                  href={monde.credit.photoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="story-credit-photo-link"
                  className="link-underline"
                >
                  {monde.credit.photo}
                </a>
              ) : (
                monde.credit.photo
              )}
              &nbsp;·&nbsp; {t("mondeDetail.daCredit")} ·{" "}
              {monde.credit.daUrl ? (
                <a
                  href={monde.credit.daUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="story-credit-da-link"
                  className="link-underline"
                >
                  {monde.credit.da}
                </a>
              ) : (
                monde.credit.da
              )}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Editorial gallery */}
      <section className="border-t border-[#E5DFD3]">
        <div className="mx-auto max-w-[1800px] px-2 md:px-6 py-16 md:py-24 grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
          {monde.gallery.map((src, i) => (
            <Reveal
              key={src}
              delay={(i % 3) * 0.08}
              y={40}
              className="overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setLightboxIdx(i)}
                data-testid={`gallery-photo-${i}`}
                aria-label={`Agrandir la photo ${i + 1}`}
                className="block w-full h-full cursor-zoom-in focus:outline-none"
              >
                <img
                  src={src}
                  alt={`${monde.name} ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover aspect-[3/4] hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <Lightbox
        images={monde.gallery}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(null)}
        onNavigate={setLightboxIdx}
      />

      {/* Next worlds */}
      <section className="border-t border-[#E5DFD3] py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <h3 className="font-serif text-3xl md:text-5xl tracking-[-0.02em] text-[#121212]">
              {t("archives.troisFiguresTitle")}
            </h3>
            <Link
              to="/archives"
              className="text-[11px] tracking-[0.28em] uppercase link-underline text-[#121212]"
              data-testid="back-to-mondes-link"
            >
              {t("archives.seeAll")}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {otherMondes.map((m) => {
              const mSubtitle = t(`mondes.${m.slug}.subtitle`, { defaultValue: m.subtitle });
              return (
                <Link
                  key={m.slug}
                  to={`/archives/${m.slug}`}
                  className="group block"
                  data-testid={`next-monde-${m.slug}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={m.cover}
                      alt={m.name}
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="mt-6 flex items-baseline justify-between">
                    <div>
                      <h4 className="font-serif text-3xl text-[#121212]">{m.name}</h4>
                      <p className="mt-1 font-serif italic text-[#5C4033]">{mSubtitle}</p>
                    </div>
                    <ArrowRight size={20} strokeWidth={1.2} className="text-[#5C4033]" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 pb-20">
        <Link
          to="/archives"
          className="inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#121212] group"
          data-testid="monde-back-link"
        >
          <ArrowLeft
            size={14}
            strokeWidth={1.2}
            className="transition-transform duration-500 group-hover:-translate-x-1"
          />
          <span className="link-underline">{t("mondeDetail.backToArchives")}</span>
        </Link>
      </div>

      {/* Subtitle accessibility — hidden tag carrying the i18n subtitle */}
      <span className="sr-only">{subtitle}</span>
    </main>
  );
}
